import { gql, useSubscription } from '@apollo/client';
import { useRideStore } from '../../stores';
import { useUserStore } from '../../stores';
import { showToast } from '../../hooks/useToast';
import { playSound } from "../../hooks/useSound";

/* ============================================
   GRAPHQL SUBSCRIPTION DEFINITIONS
   ============================================ */

// Subscription for when a new ride is created
const RIDE_CREATED = gql`
  subscription RideCreated($driverId: Int!) {
    rideCreated(driverId: $driverId) {
      id
      riderId
      riderName
      phoneNumber
      passengers
      pickUp
      dropOff
      pickUpTime
      date
      bidAmount
      waitingTime
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledAt
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

// Subscription for when ride details are updated
const RIDE_DETAILS_UPDATED = gql`
  subscription RideDetailsUpdated {
    rideDetailsUpdated {
      id
      riderId
      riderName
      phoneNumber
      passengers
      pickUp
      dropOff
      pickUpTime
      bidAmount
      waitingTime
    }
  }
`;

// Subscription for when a ride is accepted by a driver
const RIDE_ACCEPTED = gql`
  subscription RideAccepted {
    rideAccepted {
      id
      riderId
      riderName
      driverId
      driverName
      isAccepted
      pickUp
      dropOff
    }
  }
`;

// Subscription for when a ride is canceled
export const RIDE_CANCELED = gql`
  subscription RideCanceled {
    rideCanceled {
      ride {
        id
        riderId
        riderName
        driverId
        driverName
        isCanceled
        canceledAt
      }
      canceledBy
    }
  }
`;

// Subscription for when a ride is completed
const RIDE_COMPLETED = gql`
  subscription RideCompleted {
    rideCompleted {
      id
      riderId
      riderName
      driverId
      driverName
      isCompleted
      completedAt
    }
  }
`;

// Subscription for when a ride expires
const RIDE_EXPIRED = gql`
  subscription RideExpired {
    rideExpired {
      id
      riderId
      riderName
      isExpired
      expiredAt
    }
  }
`;

/* ============================================
   SUBSCRIPTION HOOKS
   ============================================ */

// Subscription for new ride requests.
export const useRideCreatedSubscription = (driverId, refetchActiveRides) => {
  const { data, loading, error } = useSubscription(RIDE_CREATED, {
    variables: { driverId: parseInt(driverId, 10) }, // Ensure it's an integer
    skip: !driverId,
    onError: (err) => {
      console.error('Subscription error:', err);
    },
    onData: ({ data: { data } }) => {
      if (data?.rideCreated) {
        showToast.info('New ride requested! 🔔');
        playSound("general");

        if (refetchActiveRides) {
          refetchActiveRides();
        }
      }
    },
  });

  return { data, loading, error };
};

// Subscription for ride detail updates.
export const useRideDetailsUpdatedSubscription = (refetchRide) => {
  const { currentRideId } = useRideStore();

  const { data, loading, error } = useSubscription(RIDE_DETAILS_UPDATED, {
    onData: ({ data: { data } }) => {
      if (data?.rideDetailsUpdated) {
        // TODO: Delete console logs.
        // console.log('Ride details updated:', data.rideDetailsUpdated.id);

        // Only refetch if it's the current ride we're viewing.
        if (
          currentRideId &&
          currentRideId === data.rideDetailsUpdated.id &&
          refetchRide
        ) {
          refetchRide();
        }
      }
    },
  });

  return { data, loading, error };
};

// Subscription for ride acceptances.
export const useRideAcceptedSubscription = (
  refetchActiveRides,
  refetchAcceptedRides,
  refetchRide,
  refetchDriver
) => {
  const { currentRideId } = useRideStore();
  const { userId } = useUserStore();

  const { data, loading, error } = useSubscription(RIDE_ACCEPTED, {
    onData: ({ data: { data } }) => {
      if (data?.rideAccepted) {
        // TODO: Delete console logs.
        // console.log('Ride accepted:', data.rideAccepted.id);

        // Driver toast: only toast if this driver accepted the ride.
        if (userId === data.rideAccepted.driverId) {
          showToast.success('Ride accepted! 🚗');
          playSound("success");
        }

        // Rider toast: only toast if this rider has their ride accepted.
        if (userId === data.rideAccepted.riderId) {
          showToast.success(
            `${data.rideAccepted.driverName} accepted your ride! 🎉`
          );
          playSound("success");
        }

        // Only call if provided and need this data needs to be refetched.
        if (refetchActiveRides) {
          refetchActiveRides();
        }

        if (refetchAcceptedRides) {
          refetchAcceptedRides();
        }

        if (refetchDriver) {
          refetchDriver();
        }

        // If this is the current ride being viewed, refetch its details
        if (currentRideId && currentRideId === data.rideAccepted.id) {
          // TODO: Delete console logs.
          // console.log('Refetching current ride data for navigation');
          refetchRide();
        }
      }
    },
  });

  return { data, loading, error };
};

// Subscription for ride cancellations.
export const useRideCanceledSubscription = (
  refetchActiveRides,
  refetchAcceptedRides,
  refetchRide,
  refetchDriver
) => {
  const { currentRideId } = useRideStore();
  const { userId } = useUserStore();

  const { data, loading, error } = useSubscription(RIDE_CANCELED, {
    onData: ({ data: { data } }) => {
      if (data?.rideCanceled) {
        const { ride, canceledBy } = data.rideCanceled;

        // Driver cancellation is now handled by RideCanceledModal (in RequireDriverAuth).
        // No toast needed here for drivers — the modal provides the notification.

        // Rider toast: only toast this rider when their accepted ride gets canceled by driver.
        if (canceledBy === 'DRIVER' && userId === ride.riderId) {
          showToast.error(`Sorry, ${ride.driverName} canceled your ride 😞`);
          playSound("alert");
        }

        // Only call if provided and need this data needs to be refetched.
        if (refetchActiveRides) {
          refetchActiveRides();
        }

        if (refetchAcceptedRides) {
          refetchAcceptedRides();
        }

        if (refetchDriver) {
          refetchDriver();
        }

        // If this is the current ride being viewed, refetch its details
        if (currentRideId && currentRideId === data.rideCanceled.ride.id && refetchRide) {
          refetchRide();
        }
      }
    },
  });

  return { data, loading, error };
};

// Subscription for ride completions.
export const useRideCompletedSubscription = (
  refetchAcceptedRides,
  refetchRide,
  refetchDriver
) => {
  const { currentRideId } = useRideStore();
  const { userId } = useUserStore();

  const { data, loading, error } = useSubscription(RIDE_COMPLETED, {
    onData: ({ data: { data } }) => {
      if (data?.rideCompleted) {
        // TODO: Delete console logs.
        // console.log('Ride completed:', data.rideCompleted.id);

        // Driver toast: only toast this driver when they complete a ride.
        if (userId === data.rideCompleted.driverId) {
          showToast.success('Ride completed! 🎉');
          playSound("success");
        }

        // Rider toast: only toast this rider when their ride is complete.
        if (userId === data.rideCompleted.riderId) {
          showToast.success('Thank you for riding with us! 🚗✨');
          playSound("success");
        }

        // Only call if provided and need this data needs to be refetched.
        if (refetchAcceptedRides) {
          refetchAcceptedRides();
        }

        if (refetchDriver) {
          refetchDriver();
        }

        // If this is the current ride being viewed, refetch its details
        if (
          currentRideId &&
          currentRideId === data.rideCompleted.id &&
          refetchRide
        ) {
          refetchRide();
        }
      }
    },
  });

  return { data, loading, error };
};

// Subscription for ride expirations.
export const useRideExpiredSubscription = (refetchActiveRides, refetchRide, refetchDriver) => {
  const { currentRideId } = useRideStore();
  const { userId } = useUserStore();

  const { data, loading, error } = useSubscription(RIDE_EXPIRED, {
    onData: ({ data: { data } }) => {
      if (data?.rideExpired) {
        // TODO: Delete console logs.
        // console.log('Ride expired:', data.rideExpired.id);

        // Toast for driver when a ride expires.
        showToast.info('A ride request has expired ⏰');
        playSound("alert");

        // Only call if provided and need this data needs to be refetched.
        if (refetchActiveRides) {
          refetchActiveRides();
        }

        if (refetchDriver) {
          refetchDriver();
        }

        // If this is the current ride being viewed, refetch its details
        if (
          currentRideId &&
          currentRideId === data.rideExpired.id &&
          refetchRide
        ) {
          refetchRide();
        }
      }
    },
  });

  return { data, loading, error };
};
